// Columna 1 de la ventana «Santi» (propuesta §5.1, P1): «Santi», el botón «Pedir algo a Santi», la fila «Chat con
// Santi» y los contratos en tres secciones — «Te toca» (siempre abierta), «Esperando a Santi» (se pliega y el navegador
// recuerda cómo la dejaste) y «Hecho esta semana» (plegada). Una cuenta gris por sección y nada más: sin cuentas
// repetidas, sin naranja por antigüedad. Dentro de cada sección, lo que se movió último arriba, como un chat (el orden no
// salta al leer). Lo que Pau acaba de dar por hecho o cancelar ya no está (store `quitados`).
// Un socio ve lo mismo salvo «Pedir algo a Santi» (no se pinta: eso sale hacia Santi como Pau).
import { ChevronDown, ChevronRight, MessageCircle, Plus } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { hace } from '../marco/estado';
import { FilaCerrado, FilaContrato } from './FilaContrato';
import { AvatarPersona, PuntoNuevo } from './piezas';
import { useHablaComoPau, usePelotasStore } from './store';
import { cuandoFila, nombreDe, YO } from './tiempo';
import type { ContratoDTO, HiloAbierto, ListaContratosDTO } from './tipos';

const CLAVE_PLEGADA = 'oficina:santi:esperando-plegada';

function leerPlegada(): boolean {
  try {
    return localStorage.getItem(CLAVE_PLEGADA) === '1';
  } catch {
    return false;
  }
}

function guardarPlegada(v: boolean): void {
  try {
    if (v) localStorage.setItem(CLAVE_PLEGADA, '1');
    else localStorage.removeItem(CLAVE_PLEGADA);
  } catch {
    // Sin almacenamiento: se recuerda mientras dure la visita.
  }
}

const porUltimo = (a: ContratoDTO, b: ContratoDTO) => Date.parse(b.ultimoEn) - Date.parse(a.ultimoEn);

/** Los contratos que enseña la lista, por secciones y en su orden (también lo usa la ventana: «el siguiente queda abierto»). */
export function seccionesDe(lista: ListaContratosDTO | null, quitados: Record<number, true>): { teToca: ContratoDTO[]; esperando: ContratoDTO[] } {
  const vivos = (lista?.contratos ?? []).filter((c) => !quitados[c.temaId]).sort(porUltimo);
  return { teToca: vivos.filter((c) => c.lado === YO), esperando: vivos.filter((c) => c.lado !== YO) };
}

export function ListaSanti({
  lista,
  errorLista = false,
  activo,
  alAbrir,
  corto = false,
  className,
}: {
  lista: ListaContratosDTO | null;
  errorLista?: boolean;
  activo: HiloAbierto;
  alAbrir: (hilo: HiloAbierto) => void;
  /** iPad (por debajo de 1280): las fechas de fila en corto («El jueves»). */
  corto?: boolean;
  className?: string;
}) {
  const comoPau = useHablaComoPau();
  const quitados = usePelotasStore((s) => s.quitados);
  const [plegada, setPlegada] = useState(leerPlegada);
  const [verHechos, setVerHechos] = useState(false);
  const saliendo = useSaliendo(quitados);
  // Lo que se acaba de quitar sigue pintado 150 ms mientras sale (propuesta §5.6); después, fuera.
  const ocultos = useMemo(() => {
    const o: Record<number, true> = { ...quitados };
    for (const id of Object.keys(saliendo)) delete o[Number(id)];
    return o;
  }, [quitados, saliendo]);
  const { teToca, esperando } = seccionesDe(lista, ocultos);
  const cerrados = lista?.cerradosSemana ?? [];
  const vacia = !!lista && teToca.length === 0 && esperando.length === 0;
  // A quién se espera: el nombre de quien tiene el turno (hoy, siempre Santi).
  const otro = esperando[0]?.lado ?? 'santi';

  return (
    <aside className={cn('flex min-h-0 min-w-0 flex-col border-r border-oficina-borde bg-oficina-panel', className)} data-testid="lista-santi">
      <header className="flex h-[72px] shrink-0 items-center gap-3 px-4">
        <AvatarPersona persona="santi" tamano={40} />
        <h2 className="text-[18px] font-semibold leading-6 text-oficina-texto">{t('oficinaV2.pelotas.santi')}</h2>
      </header>
      {comoPau ? (
        <div className="shrink-0 px-3 pb-3">
          <button
            type="button"
            onClick={() => alAbrir('pedir')}
            aria-current={activo === 'pedir' ? 'true' : undefined}
            className={cn(
              'flex h-11 w-full items-center justify-center gap-2 rounded-[10px] text-[16px] font-semibold text-oficina-texto outline-none ring-1 ring-inset transition-colors focus-visible:ring-2 focus-visible:ring-white/85',
              activo === 'pedir' ? 'bg-white/10 ring-white/[0.18]' : 'bg-white/[0.06] ring-white/10 hover:bg-white/10',
            )}
            data-testid="pedir-algo"
          >
            <Plus className="h-[18px] w-[18px]" aria-hidden />
            {t('oficinaV2.pelotas.lista.pedirAlgo')}
          </button>
        </div>
      ) : null}
      <nav aria-label={t('oficinaV2.pelotas.lista.titulo')} className="min-h-0 flex-1 overflow-y-auto px-2 pb-2 [scrollbar-width:thin]">
        {lista?.centroCaido || (errorLista && !lista) ? (
          <p className="px-3 pb-2 text-[14px] leading-5 text-oficina-texto3" data-testid="lista-sin-conexion">
            {lista ? t('oficinaV2.pelotas.lista.sinConexionDesde', { hace: hace(lista.servidoEn) }) : t('oficinaV2.pelotas.lista.sinConexion')}
          </p>
        ) : null}
        <FilaChat lista={lista} activo={activo === 'general'} alAbrir={() => alAbrir('general')} />
        <div className="mx-3 my-2 h-px bg-white/[0.06]" aria-hidden />

        {!lista ? (
          errorLista ? null : <Esqueleto />
        ) : (
          <>
            {vacia ? (
              <div className="px-6 py-8 text-center text-oficina-texto2" data-testid="lista-vacia">
                <p className="text-[16px] font-semibold leading-[22px]">{t('oficinaV2.pelotas.lista.vacioTitulo')}</p>
                <p className="mt-1 text-[15px] leading-[22px]">{t('oficinaV2.pelotas.lista.vacioTexto')}</p>
              </div>
            ) : null}
            {teToca.length ? (
              <section aria-label={t('oficinaV2.pelotas.lista.teToca')} data-testid="seccion-te-toca">
                <h3 className={SECCION}>
                  {t('oficinaV2.pelotas.lista.teToca')}
                  <span className={CUENTA}>{teToca.length}</span>
                </h3>
                <ul>
                  {teToca.map((c) => (
                    <li key={c.temaId}>
                      <FilaContrato c={c} activo={activo === c.temaId} alAbrir={() => alAbrir(c.temaId)} corto={corto} saliendo={!!saliendo[c.temaId] && !!quitados[c.temaId]} />
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
            {esperando.length ? (
              <section aria-label={t('oficinaV2.pelotas.lista.esperandoA', { quien: nombreDe(otro) })} data-testid="seccion-esperando">
                <button
                  type="button"
                  onClick={() => {
                    guardarPlegada(!plegada);
                    setPlegada(!plegada);
                  }}
                  aria-expanded={!plegada}
                  className={cn(SECCION, 'rounded-[10px] outline-none hover:bg-white/[0.04] focus-visible:ring-2 focus-visible:ring-oficina-enlace/70')}
                  data-testid="seccion-esperando-boton"
                >
                  {t('oficinaV2.pelotas.lista.esperandoA', { quien: nombreDe(otro) })}
                  <span className={CUENTA}>{esperando.length}</span>
                  {plegada ? <ChevronRight className={CHEVRON} aria-hidden /> : <ChevronDown className={CHEVRON} aria-hidden />}
                </button>
                {plegada ? null : (
                  <ul>
                    {esperando.map((c) => (
                      <li key={c.temaId}>
                        <FilaContrato c={c} activo={activo === c.temaId} alAbrir={() => alAbrir(c.temaId)} corto={corto} saliendo={!!saliendo[c.temaId] && !!quitados[c.temaId]} />
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ) : null}
            {cerrados.length ? (
              <section aria-label={t('oficinaV2.pelotas.lista.hechoSemana')} data-testid="seccion-hechos">
                <button
                  type="button"
                  onClick={() => setVerHechos((v) => !v)}
                  aria-expanded={verHechos}
                  className={cn(SECCION, 'rounded-[10px] text-oficina-texto3 outline-none hover:bg-white/[0.04] focus-visible:ring-2 focus-visible:ring-oficina-enlace/70')}
                  data-testid="seccion-hechos-boton"
                >
                  {t('oficinaV2.pelotas.lista.hechoSemana')}
                  <span className={CUENTA}>{cerrados.length}</span>
                  {verHechos ? <ChevronDown className={CHEVRON} aria-hidden /> : <ChevronRight className={CHEVRON} aria-hidden />}
                </button>
                {verHechos ? (
                  <ul>
                    {cerrados.map((c) => (
                      <li key={c.temaId}>
                        <FilaCerrado c={c} activo={activo === c.temaId} alAbrir={() => alAbrir(c.temaId)} />
                      </li>
                    ))}
                  </ul>
                ) : null}
              </section>
            ) : null}
          </>
        )}
      </nav>
    </aside>
  );
}

/** Los contratos que acaban de quitarse («Hecho», «Cancelar»), durante los 150 ms de su salida. */
function useSaliendo(quitados: Record<number, true>): Record<number, true> {
  const [saliendo, setSaliendo] = useState<Record<number, true>>({});
  const antes = useRef(quitados);
  const relojes = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  useEffect(() => {
    const nuevos = Object.keys(quitados)
      .map(Number)
      .filter((id) => !antes.current[id]);
    antes.current = quitados;
    if (!nuevos.length) return;
    setSaliendo((s) => ({ ...s, ...Object.fromEntries(nuevos.map((id) => [id, true as const])) }));
    relojes.current.push(
      setTimeout(
        () =>
          setSaliendo((s) => {
            const c = { ...s };
            for (const id of nuevos) delete c[id];
            return c;
          }),
        SALIDA_MS,
      ),
    );
  }, [quitados]);
  useEffect(() => {
    const r = relojes.current;
    return () => r.forEach(clearTimeout);
  }, []);
  return saliendo;
}

/** La salida de una fila (`duration-motion-default`). */
const SALIDA_MS = 150;

const SECCION = 'mt-1 flex h-11 w-full items-center gap-2 px-3 text-left text-[14px] font-semibold leading-5 text-oficina-texto2';
const CUENTA = 'font-medium text-oficina-texto3 tabular-nums';
const CHEVRON = 'ml-auto h-4 w-4 text-oficina-texto3';

/** La fila «Chat con Santi»: la conversación con la persona, arriba de todo. «Santi: …» / «Tú: …» y el punto si hay algo suyo sin ver. */
function FilaChat({ lista, activo, alAbrir }: { lista: ListaContratosDTO | null; activo: boolean; alAbrir: () => void }) {
  const ultimo = lista?.general.ultimo ?? null;
  const nuevo = (lista?.general.nuevos ?? 0) > 0;
  const quien = ultimo ? (ultimo.de === YO ? t('oficinaV2.pelotas.tu') : nombreDe(ultimo.de)) : '';
  return (
    <button
      type="button"
      onClick={alAbrir}
      aria-current={activo ? 'true' : undefined}
      className={cn(
        'flex w-full items-center gap-3 rounded-[10px] px-3 py-2.5 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-oficina-enlace/70',
        activo ? 'bg-white/[0.07] ring-1 ring-inset ring-white/[0.08]' : 'hover:bg-white/[0.04]',
      )}
      data-testid="fila-chat"
      data-nuevo={nuevo ? 'true' : undefined}
    >
      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-oficina-hover text-oficina-texto ring-[1.5px] ring-inset ring-white/15" aria-hidden>
        <MessageCircle className="h-5 w-5" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex min-w-0 items-baseline gap-3">
          <span className="min-w-0 flex-1 truncate text-[16px] font-semibold leading-[22px] text-oficina-texto">{t('oficinaV2.pelotas.lista.chat')}</span>
          {ultimo ? <span className={cn('shrink-0 text-[14px] leading-[22px] tabular-nums', nuevo ? 'font-semibold text-oficina-texto' : 'text-oficina-texto3')}>{cuandoFila(ultimo.en)}</span> : null}
        </span>
        {ultimo ? (
          <span className="flex min-w-0 items-center gap-3">
            <span className={cn('min-w-0 flex-1 truncate text-[15px] leading-5', nuevo ? 'text-oficina-texto2' : 'text-oficina-texto3')}>{t('oficinaV2.pelotas.lista.deQuien', { quien, texto: ultimo.texto.replace(/\s+/g, ' ') })}</span>
            {nuevo ? <PuntoNuevo /> : null}
          </span>
        ) : null}
      </span>
    </button>
  );
}

/** Cargando: tres filas de dos barras grises, sin texto. */
function Esqueleto() {
  return (
    <ul aria-busy="true" aria-label={t('oficinaV2.pelotas.lista.cargando')} data-testid="lista-cargando">
      {[0, 1, 2].map((i) => (
        <li key={i} className="flex flex-col gap-2 px-3 py-3">
          <span className="h-3.5 w-3/5 animate-pulse rounded bg-white/[0.06]" />
          <span className="h-3 w-4/5 animate-pulse rounded bg-white/[0.04]" />
        </li>
      ))}
    </ul>
  );
}
